/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {CreditItem, PackagesItemByPerson} from "types/entities/PackagesItems";
import {useBookingPackagesRowInitialization} from "../hooks/useBookingPackagesRowInitialization";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {PackageCreditItemType} from "types/enums/PackageCreditItemType";
import Dates from "utils/Dates";
import BookingPackagesTicketsSelectFamily from "./BookingPackagesTicketsSelectFamily";
import BookingPackagesCreditsSelectFamily from "./BookingPackagesCreditsSelectFamily";
import FeatherIcon from "components/FeatherIcon";
import PackageCreditInfoPillow
    from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditInfoPillow";
import PackageCreditPersonsPillow
    from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditPersonsPillow";
import PackageCreditBadge from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditBadge";
import PackageCreditTextBlock from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditTextBlock";
import PackageCreditCalculatorButton
    from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditCalculatorButton";
import PackageTicketsBadge from "pages/Front/Booking/components/BookingPackageNew/components/PackageTicketsBadge";
import PackageDottedSeparator from "pages/Front/Booking/components/BookingPackageNew/components/PackageDottedSeparator";

interface BookingPackagesCreditsFamilyDriverRowProps {
    category?: PackageCategory | null;
}

/**
 * Unified family booking row.
 * One duration selection applies to all persons (adults → adult tickets, children → child tickets).
 * Family credits are assigned to the first adult.
 * Same layout for all screen sizes.
 */
const BookingPackagesCreditsFamilyDriverRow = (props: BookingPackagesCreditsFamilyDriverRowProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const availableCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.availableCreditItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);

    const {setPackageCreditItemsHandler, getPackageItemByDuration, getPackageCreditItemById} = usePackageItemKartingOptions();
    const {chosenDuration, handleDurationChange} = useBookingPackagesRowInitialization();

    const peopleCount = filter.adults + filter.children;

    // Family credits only
    const creditItems = availableCreditItems.filter(item => item?.article?.creditPackage?.category === PackageCreditItemType.Family);

    const customOrderCreditItems = [96, 128, 256, 22, 36, 64];
    const sortedPackageCreditItems = creditItems.sort((a, b) => {
        const getVal = (item: CreditItem) => {
            const amount = item.article?.creditPackage?.amount_total ?? item.article?.article_value;
            const num = Math.round(Number(String(amount).replace(",", ".").trim()));

            if (!isNaN(num) && customOrderCreditItems.includes(num)) return num;

            const name = item.article?.creditPackage?.name || "";
            const matches = name.match(/\d+/g);
            if (matches) {
                for (const m of matches) {
                    const n = parseInt(m, 10);
                    if (customOrderCreditItems.includes(n)) return n;
                }
            }

            return !isNaN(num) ? num : -1;
        };

        const aVal = getVal(a);
        const bVal = getVal(b);
        const aIndex = customOrderCreditItems.indexOf(aVal);
        const bIndex = customOrderCreditItems.indexOf(bVal);
        return (aIndex > -1 ? aIndex : 999) - (bIndex > -1 ? bIndex : 999);
    });

    // Credits are stored on the first adult (personIndex 0)
    const firstPersonCreditEntry = selectedPackageCreditItems.find(item => item.personIndex === 0);
    const selectedPackageCreditItem = getPackageCreditItemById(firstPersonCreditEntry?.id || null);

    const handlePackageCreditItemChange = (creditItem: CreditItem | null) => {
        const newItems: PackagesItemByPerson[] = creditItem?.id
            ? [{personIndex: 0, id: creditItem.id, person_type: PackageItemPersonType.Adult, quantity: 1}]
            : [{personIndex: 0, id: null, person_type: PackageItemPersonType.Adult, quantity: 0}];
        setPackageCreditItemsHandler(newItems);
    };

    return (
        <div className={"BookingPackagesCreditsFamilyDriverRow w-full flex flex-col gap-3 md:gap-5"}>
            {/* Tickets section */}
            <div className={"flex flex-col lg:flex-row items-center lg:items-stretch justify-center gap-4 lg:gap-6 xl:gap-8"}>
                {/* Info column */}
                <div className={"w-full lg:w-[260px] xl:w-[295px] shrink-0 flex flex-col justify-center items-center lg:items-start gap-2 lg:gap-4"}>
                    <PackageTicketsBadge />
                    <PackageCreditInfoPillow color={"yellow"} title={t("Entry includes unlimited use of over 20 standard attractions.")} subtitle={""} />
                    <PackageCreditPersonsPillow peopleCount={peopleCount} />
                </div>

                {/* Chevron column — fixed width so it lines up with the credits chevron and the plus */}
                <div className={"hidden lg:flex w-[24px] shrink-0 items-center justify-center text-mid-grey"}>
                    <FeatherIcon name={"chevrons-right"} className={"size-[24px]"} strokeWidth={3} />
                </div>

                {/* Cards column */}
                <div className={"w-full lg:flex-1 min-w-0 flex justify-center"}>
                    <BookingPackagesTicketsSelectFamily
                        disabled1Hour={Dates.hour(selectedTimeslot?.start_at) < 18}
                        disabled2Hours={false}
                        chosenDuration={chosenDuration}
                        setChosenDuration={handleDurationChange}
                    />
                </div>
            </div>

            <PackageDottedSeparator />

            {/* Credits section */}
            <div className={"flex flex-col lg:flex-row items-center lg:items-stretch justify-center gap-4 lg:gap-6 xl:gap-8"}>
                {/* Info column */}
                <div className={"w-full lg:w-[260px] xl:w-[295px] shrink-0 flex flex-col justify-center items-center lg:items-start gap-2 lg:gap-4"}>
                    <PackageCreditBadge />
                    <PackageCreditInfoPillow
                        color={"red"}
                        title={t("For the attractions of the Speed & Drift Zone") + ":"}
                        subtitle={"Go-Karting · Crazy Drift"}
                    />

                    <PackageCreditTextBlock
                        title={t("A shared credit for the whole family.")}
                        subtitle={t("Distribute it freely during online check-in or on-site — even all for one person.")}
                    />

                    <div>
                        <PackageCreditCalculatorButton />
                    </div>
                </div>

                {/* Chevron column */}
                <div className={"hidden lg:flex w-[24px] shrink-0 items-center justify-center text-mid-grey"}>
                    <FeatherIcon name={"chevrons-right"} className={"size-[24px]"} strokeWidth={3} />
                </div>

                {/* Cards column */}
                <div className={"w-full lg:flex-1 min-w-0 flex flex-col justify-center gap-2"}>
                    <BookingPackagesCreditsSelectFamily
                        sortedPackageCreditItems={sortedPackageCreditItems}
                        selectedPackageCreditItem={selectedPackageCreditItem}
                        handlePackageCreditItemChange={handlePackageCreditItemChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default BookingPackagesCreditsFamilyDriverRow;
